/** Data attribute used for identifying elements in Sentry breadcrumbs. */
const BREADCRUMB_ATTRIBUTE = 'data-testid';

/**
 * Interactive elements selector for breadcrumb label extraction.
 * Prioritizes buttons, links, inputs, labels, and elements with role="button".
 */
const INTERACTIVE_SELECTOR = `button[${BREADCRUMB_ATTRIBUTE}], a[${BREADCRUMB_ATTRIBUTE}], [role="button"][${BREADCRUMB_ATTRIBUTE}], label[${BREADCRUMB_ATTRIBUTE}], input[${BREADCRUMB_ATTRIBUTE}]`;

/** Selector for any element with the breadcrumb attribute. */
const ANY_ELEMENT_SELECTOR = `[${BREADCRUMB_ATTRIBUTE}]`;

/**
 * Extracts breadcrumb label from a clicked element for Sentry breadcrumbs.
 *
 * Priority:
 * 1. Closest interactive element (button, a, role="button") with the attribute
 * 2. Closest any element with the attribute
 *
 * This ensures we get the button's identifier rather than internal elements like labels or icons.
 *
 * @param target - The event target element (can be HTMLElement or SVGElement)
 * @returns The attribute value or null if not found
 */
export const getClickBreadcrumbTestId = (target: unknown): string | null => {
	if (!(target instanceof Element)) {
		return null;
	}

	const interactiveElement = target.closest(INTERACTIVE_SELECTOR);
	if (interactiveElement) {
		return interactiveElement.getAttribute(BREADCRUMB_ATTRIBUTE);
	}

	const anyElementWithAttribute = target.closest(ANY_ELEMENT_SELECTOR);
	if (anyElementWithAttribute) {
		return anyElementWithAttribute.getAttribute(BREADCRUMB_ATTRIBUTE);
	}

	return null;
};
